(ns nosco.richtext.plugins.placeholder
  (:require
   ["prosemirror-model" :as prosemirror-model]
   ["prosemirror-state" :as prosemirror-state]
   ["prosemirror-view" :as prosemirror-view]
   [hx.react :as hx :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.icons2 :as icons2]
   [nosco.richtext.util :as richtext-util]))

(defonce placeholder-plugin-key
  (prosemirror-state/PluginKey. "nosco-placeholder"))

(defnc ImagePlaceholder [{:keys [src width height]}]
  [:figure {:class ["nosco-richtext-editor-placeholder-image"]}
   [:div {:class ["nosco-richtext-editor-placeholder-image__inner"]}
    [:img {:src src :width width :height height}]]
   [:div {:class ["nosco-richtext-editor-placeholder-image__spinner"]}
    [icons2/LoadingIcon
     {:size :large
      :color colors/neutral-300
      :class ["nosco-animation-loading-rotate-360"]}]]])

(defnc VideoPlaceholder [_]
  [:div {:class ["nosco-richtext-editor-placeholder-video"]}
   [icons2/LoadingIcon
    {:size :large
     :color colors/neutral-300
     :class ["nosco-animation-loading-rotate-360"]}]])

(defnc FilePlaceholder [{:keys [name]}]
  [:span {:class ["nosco-richtext-editor-placeholder-file"]}
   [icons2/LoadingIcon
    {:class ["nosco-richtext-editor-placeholder-file__spinner" "nosco-animation-loading-rotate-360"]
     :color colors/neutral-300}]
   name])

(defnc Placeholder [{:keys [type props]}]
  (case type
    :image (hx/f [ImagePlaceholder props])
    :video (hx/f [VideoPlaceholder props])
    :file (hx/f [FilePlaceholder props])))

(defn- handle-add-action [state ^js tr ^js action]
  (let [;; Passing the DOM node directly to the decoration seems to cause problems
        ;; when the decoration is moved in the document. However, it works when
        ;; passing a constructor function. Put the destroy fn in a volatile so it
        ;; can be passed to the decoration.
        destroy-fn-ref (volatile! nil)
        create-fn (fn []
                    (let [node (.createElement js/document
                                               (case (.-placeholderType action)
                                                 :file "span"
                                                 "div"))
                          el (hx/f [Placeholder
                                    {:type (.-placeholderType action)
                                     :props (.-props action)}])]
                      (vreset! destroy-fn-ref (richtext-util/render-into-dom-node node el))
                      node))
        decoration (.widget prosemirror-view/Decoration (.-pos action)
                            create-fn
                            #js {:id (.-id action)
                                 :destroy (fn [] (when @destroy-fn-ref (@destroy-fn-ref)))})]
    (update state :placeholders #(.add ^js % (.-doc tr) #js [decoration]))))

(defn- handle-remove-action [{:keys [placeholders] :as state} _tr action]
  (let [decorations (.find ^js placeholders nil nil (fn [decoration-spec] (= (.-id action) (.-id decoration-spec))))]
    (update state :placeholders #(.remove ^js % decorations))))

(defn placeholder-plugin []
  (prosemirror-state/Plugin.
   #js {:key placeholder-plugin-key

        :state
        #js {:init
             (fn []
               {:placeholders (.-empty prosemirror-view/DecorationSet)})

             :apply
             (fn [^js tr state]
               (let [action (some-> (.getMeta tr placeholder-plugin-key) .-action)
                     action-type (some-> action .-type)]
                 (cond-> state
                   :always (update :placeholders #(.map ^js % (.-mapping tr) (.-doc tr)))
                   (= "add" action-type) (handle-add-action tr action)
                   (= "remove" action-type) (handle-remove-action tr action))))}

        :props
        #js {:decorations
             (fn [editor-state]
               (:placeholders (.getState placeholder-plugin-key editor-state)))}}))

(defn- make-placeholder-id []
  (str (random-uuid)))

(defn- insert-placeholder-tr [^js view pos id type props]
  (.. view -state -tr
      (setMeta placeholder-plugin-key
               #js {:action #js {:type "add"
                                 :id id
                                 :pos pos
                                 :placeholderType type
                                 :props props}})))

(defn insert-image-placeholder!
  "Insert a placeholder for an image at pos. Returns an ID which can be
  used to replace the placeholder later."
  [view pos src width height]
  (let [id (make-placeholder-id)
        tr (insert-placeholder-tr view
                                  pos
                                  id
                                  :image
                                  {:src src
                                   :width width
                                   :height height})]
    (.dispatch view tr)
    id))

(defn insert-video-placeholder!
  "Insert a placeholder for a video at pos. Returns an ID which can be
  used to replace the placeholder later."
  [view pos]
  (let [id (make-placeholder-id)
        tr (insert-placeholder-tr view pos id :video nil)]
    (.dispatch view tr)
    id))

(defn insert-file-placeholder!
  "Insert a placeholder for a file link at pos. Returns an ID which can
  be used to replace the placeholder later."
  [view pos name]
  (let [id (make-placeholder-id)
        tr (.. (insert-placeholder-tr view pos id :file {:name name})
               ;; Insert space after so that it's still possible to
               ;; edit while uploading
               (insertText " " pos pos))]
    (.dispatch view tr)
    id))

(defn- get-placeholder
  "Returns the placeholder Decoration for `id`, or nil if not found."
  [^js view id]
  (let [state (.. placeholder-plugin-key (getState (.-state view)))]
    (first (.find ^js (:placeholders state)
                  nil
                  nil
                  #(= id (.-id %))))))

(defn remove-placeholder!
  "Remove the placeholder, without putting anything in its place"
  [^js view id]
  (let [tr (.. view -state -tr
               (setMeta placeholder-plugin-key
                        #js {:action #js {:type "remove"
                                          :id id}}))]
    (.dispatch view tr)
    nil))

(defn- replace-placeholder-tr [view placeholder content]
  (.. ^js view -state -tr
      (insert (.-from placeholder) content)
      (setMeta placeholder-plugin-key
               #js {:action
                    #js {:type "remove"
                         :id (.. placeholder -spec -id)}})))

(defn replace-image-placeholder!
  "Replaces an image placeholder having `id` with `figure-node`."
  [view id figure-node]
  (when-let [placeholder (get-placeholder view id)]
    (let [fragment (richtext-util/figure-insertion-fragment
                    view
                    figure-node
                    (.-from placeholder))
          tr (replace-placeholder-tr view placeholder fragment)]
      (.dispatch view tr)
      nil)))

(defn replace-file-placeholder!
  "Replaces a file placeholder having `id` with `file-link-node`."
  [view id file-link-node]
  (when-let [placeholder (get-placeholder view id)]
    (let [tr (replace-placeholder-tr view placeholder file-link-node)]
      (.dispatch view tr)
      nil)))

(defn replace-video-placeholder!
  "Replaces a video placeholder having `id` with `figure-node`."
  [view id figure-node]
  (when-let [placeholder (get-placeholder view id)]
    (let [fragment (richtext-util/figure-insertion-fragment
                    view
                    figure-node
                    (.-from placeholder))
          tr (replace-placeholder-tr view placeholder fragment)]
      (.dispatch view tr)
      nil)))
